/**
 * Integer cell component
 *
 * Custom cell for ag-grid that shows a integer input.
 */
import {
  Component,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  Injector,
  Type,
  InjectionToken,
  AbstractType,
} from '@angular/core';

import { AgRendererComponent } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import {
  get,
  includes,
  isEmpty,
  isEqual,
  isFunction,
  isNil,
  isNumber,
  toLower,
  values,
} from 'lodash';

import {
  KEY_CODES,
  BLOCK_NAVIGATION_KEY_CODES,
  DEFAULT_ARTIFICIAL_INPUT_THRESHOLD,
} from '@config/global-constants';
import {
  CUSTOM_XUX_GRID_ACTIONS,
  EInputEvent,
  IArtificialInputWatcherOption,
  XUX_GRID_ACTIONS,
  XuxGridAction,
} from '@shared/types';
import { setFocusedCell } from '@app/components/xvi-grid/decorators/set-focused-cell.decorator';
import { Subject } from '@app/shared/rx';
import { artificialInputWatcher, judgeScanned } from '@app/shared/utils';
import { ScanningService } from '@app/shared/services/scanning/scanning.service';
import { lazyInject } from '@app/containers/base/decorators/lazy-inject.decorator';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xvi-integer-renderer',
  templateUrl: 'integer-renderer.component.html',
  styleUrls: ['integer-renderer.component.scss'],
})
export class XVIIntegerRendererComponent
  implements AgRendererComponent, AfterViewInit, OnDestroy
{
  @ViewChild('countInput', { static: false }) public countInput: ElementRef;
  @ViewChild('plusButton', { static: false }) public plusButton: ElementRef;
  @ViewChild('minusButton', { static: false }) public minusButton: ElementRef;
  @lazyInject(ScanningService) public scanningService: ScanningService;

  public actionType: XUX_GRID_ACTIONS = XUX_GRID_ACTIONS.ITEM_DATA_CHANGED;
  public value: number | string = null;
  public step: number = 1;
  public min: number = 0;
  public max: number = Number.MAX_SAFE_INTEGER;
  public placeholder: string = '';
  public class: string = '';
  public disabled: boolean = false;
  public decreaseButtonDisabled: boolean;
  public increaseButtonDisabled: boolean;
  public precision: number;
  public shouldLockExitEditingMode: boolean = false;
  public allowValueUpdate: boolean = false;
  public hideButtons: boolean = false;
  public valueBeforeEdit: number | string = null;
  public lastEmittedValue: number | string = null;
  public isCellFocused: boolean = false;

  protected cancelSubscription$ = new Subject<void>();

  protected focusInputTimeout: NodeJS.Timeout = null;
  public keyTimes: number[] = [];

  protected artificialInputCheck:
    | {
        artificialInputThreshold?: number;
      }
    | boolean = false;

  protected get artificialInputThreshold() {
    return get(
      this.artificialInputCheck,
      'artificialInputThreshold',
      DEFAULT_ARTIFICIAL_INPUT_THRESHOLD,
    );
  }

  private emitItemAction: Function;
  private setLastButtonClickType: Function;
  private params: ICellRendererParams;

  public agInit(params: ICellRendererParams): void {
    this.params = params;

    this.artificialInputCheck = get(
      params,
      'artificialInputCheck',
      this.artificialInputCheck,
    );
    this.placeholder = get(params, 'placeholder', this.placeholder);
    // Use ag-cell-focus class, since getCellFocused() method was added to Ag Grid in version 23.1.0.
    this.isCellFocused = get(params, 'eGridCell')
      ? params.eGridCell.classList.contains('ag-cell-focus')
      : false;
    this.refresh(params);

    this.step = get(this.params, 'step', this.step);
    this.precision = get(this.params, 'precision', this.precision);
    this.min = get(this.params, 'min', this.min);
    this.class = get(this.params, 'class', this.class);
    this.disabled = get(this.params, 'disabled', this.disabled);
    this.lastEmittedValue = this.value;
    this.emitItemAction = get(this.params, 'context.emitItemAction');
    this.setLastButtonClickType = get(
      this.params,
      'setLastButtonClickType',
      null,
    );

    this.calculateButtonsDisabling();
  }

  public constructor(public injector: Injector) {}

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public refresh(params: ICellRendererParams): boolean {
    this.value = params?.value ?? this.value;
    if (!isNil(this.value)) this.valueBeforeEdit = Number(this.value);
    this.hideButtons = get(params, 'hideButtons', false);
    this.calculateButtonsDisabling();

    return true;
  }

  public ngAfterViewInit(): void {
    const getLastButtonClickType = get(
      this.params,
      'getLastButtonClickType',
      null,
    );
    const buttonClickType = isFunction(getLastButtonClickType)
      ? getLastButtonClickType()
      : '';
    if (this.isCellFocused && !isEmpty(buttonClickType)) {
      const buttonToFocus =
        buttonClickType === KEY_CODES.MINUS
          ? this.minusButton
          : this.plusButton;

      // Use requestAnimationFrame to prevent layout thrashing and ensure the button is rendered and visible in the DOM
      requestAnimationFrame(() => {
        buttonToFocus?.nativeElement?.focus();
      });
    }
    if (this.artificialInputCheck) {
      const artificialInputWatcherOption: IArtificialInputWatcherOption = {
        nativeElement: this.countInput.nativeElement,
        cancelSubscription$: this.cancelSubscription$,
        artificialInputThreshold: this.artificialInputThreshold,
      };

      artificialInputWatcher(
        EInputEvent.KEYDOWN,
        {
          ...artificialInputWatcherOption,
          eventHandler: this.onKeydown.bind(this),
          updateValueBeforeEventHandler: true,
          passByKeyList: [KEY_CODES.TAB, KEY_CODES.CLEAR],
          scanningService: this.scanningService,
        },
        this.setNewValue.bind(this),
        this.inputCheckValid.bind(this),
      );

      artificialInputWatcher(
        EInputEvent.KEYUP,
        {
          ...artificialInputWatcherOption,
          passByKeyList: [KEY_CODES.TAB, KEY_CODES.ENTER, KEY_CODES.CLEAR],
          eventHandler: this.onKeyup.bind(this),
          scanningService: this.scanningService,
        },
        this.setNewValue.bind(this),
      );

      artificialInputWatcher(
        EInputEvent.KEYDOWN,
        {
          ...artificialInputWatcherOption,
          passByKeyList: [KEY_CODES.TAB, KEY_CODES.ENTER, KEY_CODES.CLEAR],
          scanningService: this.scanningService,
          nativeElement: this.minusButton.nativeElement,
        },
        this.setNewValue.bind(this),
      );

      artificialInputWatcher(
        EInputEvent.KEYDOWN,
        {
          ...artificialInputWatcherOption,
          passByKeyList: [KEY_CODES.TAB, KEY_CODES.ENTER, KEY_CODES.CLEAR],
          scanningService: this.scanningService,
          nativeElement: this.plusButton.nativeElement,
        },
        this.setNewValue.bind(this),
      );
    }
  }

  public setNewValue(
    event: KeyboardEvent,
    artificialInputWatcherOption: IArtificialInputWatcherOption,
    newValue: string,
  ): void {
    const { updateValueBeforeEventHandler } = artificialInputWatcherOption;
    const eventKey = get(event, 'key');

    if (updateValueBeforeEventHandler && this.inputCheckValid(eventKey)) {
      this.value = newValue;
    }

    const isValidScanning = get(
      artificialInputWatcherOption,
      'isValidScanning',
    );

    if (isValidScanning && isFunction(this.setLastButtonClickType)) {
      this.setLastButtonClickType('');
    }

    if (isValidScanning && this.isValueChanged()) {
      const action = this.createAction(false);
      this.emitItemAction(action);
    }
  }

  public ngOnDestroy(): void {
    this.keyTimes = [];
    this.cancelSubscription$.next();
  }

  public onFocusOut(event: FocusEvent): void {
    if (
      (Number(this.value) !== this.valueBeforeEdit ||
        (isEmpty(this.value?.toString()) &&
          !isEmpty(this.valueBeforeEdit?.toString()))) &&
      this.allowValueUpdate
    ) {
      this.leaveInput(event);
      this.countInput.nativeElement.blur();
    }
  }

  @setFocusedCell()
  public onFocusIn(event: KeyboardEvent): void {
    if (!isNil(this.value)) this.valueBeforeEdit = Number(this.value);

    const relatedTarget: HTMLElement = get(event, 'relatedTarget');
    const relatedTargetClassList = get(relatedTarget, 'classList');

    // avoid selected when jump form minus or plus button
    if (
      !(
        isEqual(toLower(get(relatedTarget, 'tagName')), 'button') &&
        includes(relatedTargetClassList, 'btn-counter') &&
        (includes(relatedTargetClassList, 'btn-plus') ||
          includes(relatedTargetClassList, 'btn-minus'))
      )
    ) {
      (event.target as HTMLInputElement).select();
    }

    clearTimeout(this.focusInputTimeout);
  }

  public onKeyup(
    event: KeyboardEvent,
    skipArtificialInputCheck: boolean = false,
  ): void {
    if (!skipArtificialInputCheck && this.artificialInputCheck) {
      return;
    }

    if (event.key === KEY_CODES.ENTER) {
      this.leaveInput(event, true);
    }
    this.setValue(this.getNormalizedRoundedValue(), false);
  }

  public onKeydown(
    event: KeyboardEvent,
    skipArtificialInputCheck: boolean = false,
  ): void {
    if (!skipArtificialInputCheck) {
      this.keyTimes.push(Date.now());
    }

    if (
      judgeScanned(this.keyTimes) &&
      includes([KEY_CODES.TAB, KEY_CODES.ENTER], event.key)
    ) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      return;
    }

    if (!skipArtificialInputCheck && this.artificialInputCheck) {
      return;
    }

    this.allowValueUpdate = true;
    const keyCode = event.key;
    const isBlockNavigationKey: boolean = values(
      BLOCK_NAVIGATION_KEY_CODES,
    ).includes(Number(keyCode));
    this.calculateButtonsDisabling();

    if (isBlockNavigationKey) {
      event.stopPropagation();
    }

    if (keyCode === KEY_CODES.ARROW_RIGHT || keyCode === KEY_CODES.ARROW_LEFT) {
      event.stopPropagation();
      const countInput = this.countInput.nativeElement;
      const caretPos = countInput.selectionStart;
      if (
        caretPos === countInput.value.length &&
        keyCode === KEY_CODES.ARROW_RIGHT
      ) {
        // avoid grid horizontal scroll
        event.preventDefault();
        this.allowValueUpdate = false;
        this.focusMinusOrPlusButton();
      }
    }

    // Allow only numeric symbols
    if (keyCode.length === 1 && !this.inputCheckValid(keyCode)) {
      event.preventDefault();
    }

    if (keyCode === KEY_CODES.ESCAPE) {
      this.emitExitEditingAction();
    }
  }

  public onMinusButtonKeyDown(event: KeyboardEvent): void {
    this.keyTimes.push(Date.now());
    const keyCode = event.key;
    const isMinusButtonActive =
      this.minusButton.nativeElement === document.activeElement;

    if (keyCode === KEY_CODES.ARROW_RIGHT && isMinusButtonActive) {
      event.stopPropagation();
      // avoid grid horizontal scroll
      event.preventDefault();
      this.focusPlusOrMinusButton();
    }

    if (keyCode === KEY_CODES.ARROW_LEFT && isMinusButtonActive) {
      event.stopPropagation();
      // avoid grid horizontal scroll
      event.preventDefault();

      this.focusInput({
        withTimeout: true,
      });
    }
    if (this.isPreventScanningFunctionKeys(event, isMinusButtonActive)) {
      return;
    }

    if (keyCode === KEY_CODES.ENTER && isMinusButtonActive) {
      event.stopImmediatePropagation();
      event.stopPropagation();
      event.preventDefault();
      this.decrease();
      this.calculateButtonsDisabling();

      this.focusMinusOrPlusButton();
    }
  }

  public onPlusButtonKeyDown(event: KeyboardEvent): void {
    this.keyTimes.push(Date.now());
    const keyCode = event.key;
    const isPlusButtonActive =
      this.plusButton.nativeElement === document.activeElement;

    if (keyCode === KEY_CODES.ARROW_RIGHT && isPlusButtonActive) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (keyCode === KEY_CODES.ARROW_LEFT && isPlusButtonActive) {
      event.stopPropagation();
      event.preventDefault();
      // Focus on minus button if right arrow key is pressed and plus button is currently focused
      if (!this.decreaseButtonDisabled) {
        this.minusButton.nativeElement.focus();
      } else {
        this.focusInput();
      }
    }
    if (this.isPreventScanningFunctionKeys(event, isPlusButtonActive)) {
      return;
    }

    if (keyCode === KEY_CODES.ENTER && isPlusButtonActive) {
      event.stopImmediatePropagation();
      event.stopPropagation();
      event.preventDefault();
      this.increase();
      this.calculateButtonsDisabling();

      this.focusPlusOrMinusButton();
    }
  }

  public buttonKeyup(event: KeyboardEvent): void {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  public focusInput(options?: {
    cursorPos?: number;
    withTimeout?: boolean;
  }): void {
    const countInput = this.countInput.nativeElement;
    // Focus on plus button if right arrow key is pressed and minus button is currently focused
    countInput.focus();

    const setCursor = () => {
      const countSelectionPos = isNil(get(options, 'cursorPos'))
        ? countInput.value.length
        : options.cursorPos;

      countInput.setSelectionRange(countSelectionPos, countSelectionPos);
    };

    // Set cursor position to end of input value
    if (get(options, 'withTimeout', false)) {
      clearTimeout(this.focusInputTimeout);
      this.focusInputTimeout = setTimeout(() => {
        setCursor();
      }, 0);
    } else {
      setCursor();
    }
  }

  public focusMinusOrPlusButton(): void {
    if (!this.decreaseButtonDisabled) {
      // Focus on minus button if caret position is at the end of the input field
      this.minusButton.nativeElement.focus();
      if (isFunction(this.setLastButtonClickType)) {
        this.setLastButtonClickType(KEY_CODES.MINUS);
      }
    } else if (!this.increaseButtonDisabled) {
      this.plusButton.nativeElement.focus();
      if (isFunction(this.setLastButtonClickType)) {
        this.setLastButtonClickType(KEY_CODES.PLUS);
      }
    }
  }

  public focusPlusOrMinusButton(): void {
    if (!this.increaseButtonDisabled) {
      this.plusButton.nativeElement.focus();
      if (isFunction(this.setLastButtonClickType)) {
        this.setLastButtonClickType(KEY_CODES.PLUS);
      }
    } else if (!this.decreaseButtonDisabled) {
      // Focus on minus button if caret position is at the end of the input field
      this.minusButton.nativeElement.focus();
      if (isFunction(this.setLastButtonClickType)) {
        this.setLastButtonClickType(KEY_CODES.MINUS);
      }
    }
  }

  public decrease(
    finishEditing: boolean = true,
    event: KeyboardEvent = null,
  ): void {
    if (!isNil(event)) {
      event.stopPropagation();
      this.params.api.clearFocusedCell();
    }
    const newValue = this.getCalculationData(-Math.abs(this.step));
    if (newValue < this.min) return;

    this.setValue(newValue, finishEditing);
  }

  public increase(
    finishEditing: boolean = true,
    event: KeyboardEvent = null,
  ): void {
    if (!isNil(event)) {
      event.stopPropagation();

      this.params.api.clearFocusedCell();
    }
    const newValue = this.getCalculationData(Math.abs(this.step));
    if (newValue > this.max) return;

    this.setValue(newValue, finishEditing);
  }

  public setValue(value: number, finishEditing: boolean = true): void {
    this.value = value;
    this.calculateButtonsDisabling();
    if (isFunction(this.setLastButtonClickType)) {
      this.setLastButtonClickType('');
    }

    if (finishEditing) {
      this.finishEditing();
    }
    this.keyTimes = [];
  }

  public onGridCustomFocus(event): void {
    // Added custom event to focus input with enter key in grid,
    // which will trigger a keyup event, but there is no input at this time,
    // so you need to use a lock and ignore it when the keyup event is triggered for the first time
    this.shouldLockExitEditingMode = true;
    event.target.focus();
  }

  private emitExitEditingAction(
    needValidationLock = false,
    navigateToNextRow: boolean = false,
  ): void {
    if (needValidationLock && this.shouldLockExitEditingMode) {
      this.shouldLockExitEditingMode = false;

      return;
    }
    const exitEditingAction = {
      type: CUSTOM_XUX_GRID_ACTIONS.EXIT_EDITING_MODE,
      payload: {
        navigateToNextRow,
      },
    };
    this.value = this.valueBeforeEdit;
    this.lastEmittedValue = this.valueBeforeEdit;
    this.emitItemAction(exitEditingAction);
  }

  private finishEditing(navigateToNextRow: boolean = false): void {
    const isValueChange = this.isValueChanged();
    if (!isValueChange) {
      this.emitExitEditingAction(true, navigateToNextRow);

      return;
    }
    if (isFunction(this.emitItemAction)) {
      this.emitItemAction(this.createAction(navigateToNextRow));
    }
  }

  private getCalculationData(stepDiff: number = 0): number {
    return Math.abs(this.getNormalizedRoundedValue() + stepDiff);
  }

  private calculateButtonsDisabling(): void {
    this.increaseButtonDisabled = Number(this.value) + this.step > this.max;
    this.decreaseButtonDisabled = Number(this.value) - this.step < this.min;
  }

  private createAction(navigateToNextRow: boolean): XuxGridAction {
    const hasParent = get(this.params, 'node.parent', null);
    const field: string = get(this.params, 'column.colDef.field', '');
    const rowIndex: number = get(this.params, 'node.rowIndex', -1);
    const parentRowId: number = get(this.params, 'node.parent.id', null);
    const rowId: string = get(this.params, 'node.id', parentRowId);
    const rowData = get(this.params, 'data', {});
    const parentRowData = get(this.params, 'node.parent.data', {});
    const childIndex = parentRowData?.children?.findIndex(
      (child) => child.entity_id === rowData.entity_id,
    );

    const action: XuxGridAction = {
      type: this.actionType,
      payload: {
        rowIndex,
        rowId,
        rowData,
        parentRowData,
        navigate: navigateToNextRow
          ? {
              navigateToNextRow,
            }
          : null,
        field:
          hasParent && !isNil(childIndex)
            ? `children.${childIndex}.${field}`
            : field,
        value: this.getNormalizedRoundedValue(),
      },
    };

    return action;
  }

  private getNormalizedRoundedValue(): number | null {
    if (isNaN(Number(this.value))) return 0;
    if (isEmpty(this.value?.toString()) || isNil(this.value)) return null;

    return this.precision
      ? Number(Number(this.value).toFixed(this.precision))
      : Number(this.value);
  }

  private isInputEventTargetValueEmpty(event: Event): boolean {
    return isEmpty(get(event, 'target.value', ''));
  }

  private leaveInput(event: Event, navigateToNextRow: boolean = false): void {
    // reset button type
    if (isFunction(this.setLastButtonClickType)) {
      this.setLastButtonClickType('');
    }
    if (this.isInputEventTargetValueEmpty(event)) {
      this.value = null;
    }

    this.finishEditing(navigateToNextRow);
  }

  private isValueChanged(): boolean {
    // The following are several cases where the data has changed
    // 1. this.value !== this.lastEmittedValue
    // 2. this.value !== null, this.lastEmittedValue === null
    // 3. this.value === null, this.lastEmittedValue !== null

    const changed =
      Number(this.value) !== Number(this.lastEmittedValue) ||
      (isEmpty(this.value?.toString()) &&
        !isEmpty(this.lastEmittedValue?.toString())) ||
      (!isEmpty(this.value?.toString()) &&
        isEmpty(this.lastEmittedValue?.toString()));
    const bothEmpty =
      isEmpty(this.value?.toString()) &&
      !isNumber(this.value) &&
      isEmpty(this.lastEmittedValue?.toString()) &&
      !isNumber(this.lastEmittedValue);

    return !bothEmpty && changed;
  }

  private inputCheckValid(key: string): boolean {
    return /^\d+$/.test(key);
  }

  private isPreventScanningFunctionKeys(
    event: KeyboardEvent,
    isButtonActive: boolean,
  ): boolean {
    const keyCode = event.key;
    if (
      includes([KEY_CODES.TAB, KEY_CODES.ENTER], keyCode) &&
      isButtonActive &&
      judgeScanned(this.keyTimes)
    ) {
      event.stopPropagation();
      event.preventDefault();

      return true;
    }

    return false;
  }
}
